import axios from "axios";
import pino from "pino";
import { getDefaultWebhookUrl } from "./getWebhookUrl.js";
import { buildEnhancedWebhookPayload } from "./buildWebhookPayload.js";

const { API_TOKEN } = process.env;
const logger = pino({ level: "debug" });
// Utility delay function
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const sendToWebhookAndRespond = async ({
    sessionId,
    sessionDeviceId,
    message,
    plainText,
    socket,
    mainMenuMessage = null,
    webhookUrl = null, // ✅ allow webhookUrl to be passed in
    processingMode = "webhook_flow", // webhook_flow, menu_flow, or followup_state
    fallbackReason = "webhook_triggered",
    menuConfig = null,
    userSessionData = null,
    sessionData,
    isSessionExpired = () => false,
    matchedMenuItem = null,
}) => {
    // 🧠 Validate input
    console.log("🔁 Sending to webhook:", {
        sessionId,
        processingMode,
        webhookUrl,
    });

    // 🧠 Declare url outside try block so it's accessible in catch block
    let url = webhookUrl;

    try {
        // 🧠 Use provided webhookUrl, or fallback to getting it from DB
        url = url || (await getDefaultWebhookUrl(sessionId));

        if (!url) {
            logger.warn(`⚠️ No webhook URL found for session: ${sessionId}`);
            return;
        }

        // Build enhanced webhook payload
        const userId = message.key.remoteJid;
        const webhookPayload = await buildEnhancedWebhookPayload({
            sessionId,
            sessionDeviceId,
            socket,
            message,
            plainText,
            userId,
            sessionData,
            menuConfig,
            processingMode,
            fallbackReason,
            userSessionData,
            isSessionExpired,
        });

        // Set webhook URL and matched menu item
        webhookPayload.webhookUrl = url;
        webhookPayload.matchedMenuItem = matchedMenuItem;

        const response = await axios.post(
            url,
            webhookPayload,
            {
                headers: {
                    "Content-Type": "application/json",
                    "User-Agent": "Baileys-Flowkirim/1.0",
                    Authorization: `Bearer ${API_TOKEN}`,
                },
                timeout: 30000, // 30 second timeout to prevent hanging
                validateStatus: (status) => status < 500, // Don't throw for 4xx errors
            }
        );

        const result = response.data;
        console.log("🔁 Webhook response:", result);

        if (result?.from && result?.message) {
            const textToSend = mainMenuMessage
                ? `${result.message}\n\n${mainMenuMessage}`
                : result.message;
            try {
                // Show typing indicator
                await socket.sendPresenceUpdate("composing", result.from);

                // Simulate realistic typing delay (adjust based on message length)
                const typingDelay = Math.min(
                    Math.max(textToSend.length * 50, 1000),
                    5000
                ); // 50ms per character, min 1s, max 5s
                await delay(typingDelay);

                // Stop typing indicator
                await socket.sendPresenceUpdate("paused", result.from);

                // Send the message
                await socket.sendMessage(result.from, {
                    text: textToSend,
                });
            } catch (error) {
                console.error(
                    "Error sending message with typing indicator:",
                    error
                );
                // Fallback: send message without typing indicator
                try {
                    await socket.sendMessage(result.from, {
                        text: textToSend,
                    });
                } catch (fallbackError) {
                    console.error(
                        "Fallback message send failed:",
                        fallbackError
                    );
                }
            }
        }
    } catch (error) {
        // Enhanced error handling to prevent server crash
        const errorMessage = error?.message || 'Unknown error';
        const errorCode = error?.code || 'UNKNOWN';
        const isNetworkError = errorCode === 'ECONNREFUSED' ||
                              errorCode === 'ENOTFOUND' ||
                              errorCode === 'ETIMEDOUT' ||
                              errorCode === 'ECONNRESET';

        logger.error({
            msg: `❌ Webhook request failed for session: ${sessionId}`,
            webhookUrl: url,
            error: errorMessage,
            code: errorCode,
            isNetworkError,
            response: error?.response?.data || null,
            status: error?.response?.status || null,
        });

        // Don't throw - let the server continue running
        // Just log and continue processing other messages
        return { error: errorMessage, code: errorCode };
    }
};

export { sendToWebhookAndRespond };
